/**
 * The month grid, as data — no React, no DOM, no locale.
 *
 * Kept separate from the component because every off-by-one a calendar can have lives here: the first of the
 * month landing on the wrong weekday, February in a leap year, a month that needs six rows instead of five,
 * and the day shifting by one in half the world's timezones. Those are cheap to pin as values and expensive
 * to chase through a rendered grid.
 *
 * **Everything is UTC.** A calendar is arithmetic on a date, never an instant: constructing local midnight
 * and reading it back is what makes the 1st render as the 30th for anyone west of Greenwich.
 */

/** A day in the grid. `iso` is what selecting it stores; `inMonth` is false for the padding days. */
export interface CalendarDay {
  readonly iso: string;
  readonly day: number;
  readonly inMonth: boolean;
}

/** `2026-09` — the month a grid is built for, and what the header shows. */
export interface CalendarMonth {
  readonly year: number;
  /** 1-12, not the 0-11 `Date` uses. The off-by-one that convention causes is the point of naming it. */
  readonly month: number;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

export function isoOf(year: number, month: number, day: number): string {
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** The month an ISO date falls in, or `fallback` when there is no usable date yet. */
export function monthOf(iso: string, fallback: CalendarMonth): CalendarMonth {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (match === null) return fallback;
  return { year: Number(match[1]), month: Number(match[2]) };
}

/** Today, in the kiosk's own timezone, as a month. */
export function currentMonth(): CalendarMonth {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

/**
 * Today as `YYYY-MM-DD`, in the kiosk's LOCAL timezone.
 *
 * ⚠️ Local, never UTC, and the bug is on record: `toISOString()` is UTC, so in Pennsylvania every evening
 * after 20:00 the kiosk's idea of "today" was already tomorrow — exactly the hours somebody walks in after
 * their shift. `en-CA` is the locale that formats as `YYYY-MM-DD` without hand-rolled padding.
 */
export function todayIso(): string {
  return new Date().toLocaleDateString('en-CA');
}

export function addMonths({ year, month }: CalendarMonth, delta: number): CalendarMonth {
  // Zero-based arithmetic, then back to 1-12. `Date` normalises month 12 and month -1 into the next and
  // previous year, which is why this goes through it rather than doing the wraparound by hand.
  const shifted = new Date(Date.UTC(year, month - 1 + delta, 1));
  return { year: shifted.getUTCFullYear(), month: shifted.getUTCMonth() + 1 };
}

/**
 * Six weeks of days, Sunday-first, with the neighbouring months' days as padding.
 *
 * **Always six rows, never five.** A grid that changes height between months makes the control jump under
 * the finger as somebody pages through it, and on a touch screen that means mis-taps. The cost is one row of
 * padding in most months, which is cheaper than a moving target.
 */
export function monthGrid({ year, month }: CalendarMonth): readonly CalendarDay[] {
  const first = new Date(Date.UTC(year, month - 1, 1));
  const leading = first.getUTCDay();
  const days: CalendarDay[] = [];

  for (let index = 0; index < 42; index += 1) {
    const date = new Date(Date.UTC(year, month - 1, index - leading + 1));
    days.push({
      iso: isoOf(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate()),
      day: date.getUTCDate(),
      inMonth: date.getUTCMonth() + 1 === month && date.getUTCFullYear() === year,
    });
  }

  return days;
}
